import React, { useState, useEffect } from 'react'
import { X, Save, Eye, EyeOff, User, Shield, Check } from 'lucide-react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { userService } from '../../services/userService'
import { roleService } from '../../services/roleService'
import toast from 'react-hot-toast'

interface UserFormData {
  name: string
  email: string
  password: string
  confirmPassword: string
  isActive: boolean
  roleIds: number[]
}

interface UserFormModalProps {
  isOpen: boolean
  onClose: () => void
  user?: {
    id: number
    name: string
    email: string
    isActive: boolean
    roles?: Array<{ id: number; name: string }>
  } | null
}

const UserFormModal: React.FC<UserFormModalProps> = ({ isOpen, onClose, user }) => {
  const queryClient = useQueryClient()
  const isEditing = !!user
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const [formData, setFormData] = useState<UserFormData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    isActive: true,
    roleIds: []
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Cargar roles disponibles
  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: roleService.getRoles,
    enabled: isOpen
  })

  // Reset form when modal opens/closes or user changes
  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        confirmPassword: '',
        isActive: user.isActive,
        roleIds: user.roles?.map(role => role.id) || []
      })
    } else if (isOpen) {
      setFormData({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        isActive: true,
        roleIds: []
      })
    }
    setErrors({})
  }, [isOpen, user])

  const createUserMutation = useMutation({
    mutationFn: userService.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries(['users'])
      toast.success('Usuario creado exitosamente')
      onClose()
    },
    onError: (error: any) => {
      toast.error(error.message || 'Error al crear usuario')
    }
  })

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => 
      userService.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['users'])
      toast.success('Usuario actualizado exitosamente')
      onClose()
    },
    onError: (error: any) => {
      toast.error(error.message || 'Error al actualizar usuario')
    }
  })

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    // Validar nombre
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido'
    } else if (formData.name.length < 2) {
      newErrors.name = 'El nombre debe tener al menos 2 caracteres'
    }

    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido'
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Ingresa un email válido'
    }

    // Validar contraseña (solo si no estamos editando o si se ingresó nueva contraseña)
    if (!isEditing || formData.password) {
      if (!formData.password) {
        newErrors.password = 'La contraseña es requerida'
      } else if (formData.password.length < 6) {
        newErrors.password = 'La contraseña debe tener al menos 6 caracteres'
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Las contraseñas no coinciden'
      }
    }

    // Validar roles (opcional por ahora mientras se arregla el backend)
    // if (formData.roleIds.length === 0) {
    //   newErrors.roleIds = 'Selecciona al menos un rol'
    // }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    // Parse name into firstName and lastName
    const nameParts = formData.name.trim().split(' ')
    const firstName = nameParts[0] || ''
    const lastName = nameParts.slice(1).join(' ') || ''

    // Username will be generated from name for both create and update

if (isEditing && user) {
      const updateData = {
        username: formData.name.trim(),
        firstName,
        lastName,
        email: formData.email.trim(),
        isActive: formData.isActive,
        roleIds: formData.roleIds,
        ...(formData.password && { password: formData.password })
      }
      updateUserMutation.mutate({ id: user.id, data: updateData })
    } else {
      // For new users, password is required
      if (!formData.password) {
        toast.error('Password is required for new users')
        return
      }
      const createData = {
        username: formData.name.trim(),
        firstName,
        lastName,
        email: formData.email.trim(),
        isActive: formData.isActive,
        roleIds: formData.roleIds,
        password: formData.password
      }
      createUserMutation.mutate(createData)
    }
  }

  const handleInputChange = (field: keyof UserFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleRoleToggle = (roleId: number) => {
    setFormData(prev => ({
      ...prev,
      roleIds: prev.roleIds.includes(roleId)
        ? prev.roleIds.filter(id => id !== roleId)
        : [...prev.roleIds, roleId]
    }))
    
    if (errors.roleIds) {
      setErrors(prev => ({ ...prev, roleIds: '' }))
    }
  }

  const isSubmitting = createUserMutation.isLoading || updateUserMutation.isLoading

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
              <User className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {isEditing ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
              </h2>
              <p className="text-sm text-gray-500">
                {isEditing ? 'Modifica los datos del usuario' : 'Ingresa los datos del nuevo usuario'}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: 'calc(90vh - 160px)' }}>
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Información Básica</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre Completo *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`input w-full ${errors.name ? 'border-red-300' : ''}`}
                    placeholder="Juan Pérez"
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`input w-full ${errors.email ? 'border-red-300' : ''}`}
                    placeholder="juan@ejemplo.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Password Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {isEditing ? 'Cambiar Contraseña' : 'Contraseña'}
                {isEditing && <span className="text-sm font-normal text-gray-500 ml-2">(dejar vacío para mantener actual)</span>}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isEditing ? 'Nueva Contraseña' : 'Contraseña *'}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className={`input w-full pr-10 ${errors.password ? 'border-red-300' : ''}`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-xs text-red-600">{errors.password}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isEditing ? 'Confirmar Nueva Contraseña' : 'Confirmar Contraseña *'}
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      className={`input w-full pr-10 ${errors.confirmPassword ? 'border-red-300' : ''}`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-xs text-red-600">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Roles Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Roles y Permisos *</h3>
              
              {roles && roles.length > 0 ? (
                <div className="space-y-3">
                  {roles.map((role) => (
                    <div key={role.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`role-${role.id}`}
                          checked={formData.roleIds.includes(role.id)}
                          onChange={() => handleRoleToggle(role.id)}
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                        />
                        <label htmlFor={`role-${role.id}`} className="ml-3 cursor-pointer">
                          <div className="flex items-center">
                            <Shield className="h-4 w-4 text-primary-600 mr-2" />
                            <span className="text-sm font-medium text-gray-900">{role.name}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{role.description}</p>
                        </label>
                      </div>
                      
                      {formData.roleIds.includes(role.id) && (
                        <Check className="h-5 w-5 text-green-600" />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Shield className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p>No hay roles disponibles</p>
                  <p className="text-xs text-gray-400 mt-2">
                    Por ahora puedes crear el usuario sin roles.
                    Los roles se pueden asignar después desde la página de Roles.
                  </p>
                </div>
              )}
              
              {errors.roleIds && (
                <p className="mt-2 text-xs text-red-600">{errors.roleIds}</p>
              )}
            </div>

            {/* Status */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Estado</h3>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                  Usuario activo
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Los usuarios inactivos no podrán acceder al sistema
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end items-center p-6 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="btn btn-static btn-md"
            >
              Cancelar
            </button>
            
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="btn btn-primary-static btn-md"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <div className="spinner w-4 h-4 mr-2"></div>
                  {isEditing ? 'Actualizando...' : 'Creando...'}
                </div>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {isEditing ? 'Actualizar Usuario' : 'Crear Usuario'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserFormModal
